import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Plus, Edit2, Trash2, Gift, Users, TrendingUp, Award, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/lib/utils';
import { bonusesApi } from '@/lib/adminApi';
import toast from 'react-hot-toast';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

interface Bonus {
    _id: string;
    name: string;
    type: string;
    status: 'active' | 'inactive' | 'expired';
    amount: number;
    conditions: string;
    claimed: number;
    totalPaid: number;
}

interface Stats {
    activeBonuses: number;
    totalClaimed: number;
    totalPaid: number;
    avgPerClaim: number;
}

export default function BonusManagement() {
    const [bonuses, setBonuses] = useState<Bonus[]>([]);
    const [stats, setStats] = useState<Stats>({
        activeBonuses: 0,
        totalClaimed: 0,
        totalPaid: 0,
        avgPerClaim: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [bonusesRes, statsRes] = await Promise.all([
                bonusesApi.getBonuses(),
                bonusesApi.getStats(),
            ]);
            setBonuses(bonusesRes.data.data.bonuses);
            setStats(statsRes.data.data);
        } catch (err) {
            console.error('Failed to fetch bonuses:', err);
            setError('Failed to load bonuses');
            toast.error('Failed to load bonuses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (id: string) => {
        toast.success(`Editing bonus #${id}`);
        // TODO: Implement edit modal
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this bonus?')) return;

        try {
            await bonusesApi.deleteBonus(id);
            toast.success('Bonus deleted successfully');
            fetchData();
        } catch (err) {
            console.error('Failed to delete bonus:', err);
            toast.error('Failed to delete bonus');
        }
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, 'success' | 'warning' | 'default'> = {
            active: 'success',
            inactive: 'warning',
            expired: 'default',
        };
        return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <p className="text-red-400">{error}</p>
                <Button onClick={fetchData}>Retry</Button>
            </div>
        );
    }

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Header */}
            <motion.div variants={itemVariants} className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                        <DollarSign className="w-7 h-7 text-violet-400" />
                        Bonus Management
                    </h1>
                    <p className="text-slate-400 mt-1">Manage bonus programs and rewards</p>
                </div>
                <Button variant="default">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Bonus
                </Button>
            </motion.div>

            {/* Stats Grid */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                            <Gift className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.activeBonuses}</p>
                            <p className="text-xs text-slate-400">Active Bonuses</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.totalClaimed.toLocaleString()}</p>
                            <p className="text-xs text-slate-400">Total Claims</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{formatCurrency(stats.totalPaid)}</p>
                            <p className="text-xs text-slate-400">Total Paid</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Award className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{formatCurrency(stats.avgPerClaim)}</p>
                            <p className="text-xs text-slate-400">Avg per Claim</p>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Bonuses List */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4">All Bonuses</h3>
                    {bonuses.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">
                            <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No bonuses found</p>
                            <p className="text-sm mt-1">Create your first bonus to get started</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {bonuses.map((bonus) => (
                                <div
                                    key={bonus._id}
                                    className="p-4 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h4 className="font-semibold text-lg">{bonus.name}</h4>
                                                {getStatusBadge(bonus.status)}
                                                <div className="px-2 py-1 rounded bg-slate-800 text-xs font-medium">
                                                    {bonus.type}
                                                </div>
                                            </div>
                                            <p className="text-sm text-slate-400 mb-2">{bonus.conditions || 'No conditions specified'}</p>
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl font-bold text-emerald-400">
                                                    {formatCurrency(bonus.amount)}
                                                </span>
                                                <span className="text-sm text-slate-500">per claim</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(bonus._id)}>
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(bonus._id)}>
                                                <Trash2 className="w-4 h-4 text-red-400" />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-700">
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Times Claimed</p>
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4 text-slate-400" />
                                                <p className="font-bold">{bonus.claimed.toLocaleString()}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Total Paid</p>
                                            <div className="flex items-center gap-2">
                                                <TrendingUp className="w-4 h-4 text-emerald-400" />
                                                <p className="font-bold text-emerald-400">{formatCurrency(bonus.totalPaid)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </motion.div>
        </motion.div>
    );
}
